import { PlusIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { useState } from 'react';
import styled from 'styled-components';
import {
  getWordMatrixDataFromHTML,
  updateWordMatrixHTML,
  WordMatrixData,
} from './utils';

interface WordMatrixControlPanelProps {
  sectionId: string;
  content: string;
  onContentChange: (content: string) => void;
}

const WordMatrixControlPanel = ({
  sectionId,
  content,
  onContentChange,
}: WordMatrixControlPanelProps) => {

  const webData = getWordMatrixDataFromHTML(content, sectionId);

  const [baseWordInput, setBaseWordInput] = useState(webData.baseWord);
  const [prefixColumns, setPrefixColumns] = useState<string[]>(
    webData.prefixColumns.map((col) => col.join(', ')),
  );
  const [suffixColumns, setSuffixColumns] = useState<string[]>(
    webData.suffixColumns.map((col) => col.join(', ')),
  );

  const parseCommaSeparated = (value: string): string[] => {
    return value
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
  };

  const handleSave = () => {
    const newData: WordMatrixData = {
      baseWord: baseWordInput.trim(),
      prefixColumns: prefixColumns.map(parseCommaSeparated),
      suffixColumns: suffixColumns.map(parseCommaSeparated),
    };
    const newContent = updateWordMatrixHTML(content, sectionId, newData);
    onContentChange(newContent);
  };

  const updateColumn = (
    side: 'prefix' | 'suffix',
    index: number,
    value: string,
  ) => {
    if (side === 'prefix') {
      const updated = [...prefixColumns];
      updated[index] = value;
      setPrefixColumns(updated);
    } else {
      const updated = [...suffixColumns];
      updated[index] = value;
      setSuffixColumns(updated);
    }
  };

  const addColumn = (
    side: 'prefix' | 'suffix',
    position: 'before' | 'after',
  ) => {
    if (side === 'prefix') {
      setPrefixColumns(
        position === 'before' ? ['', ...prefixColumns] : [...prefixColumns, ''],
      );
    } else {
      setSuffixColumns(
        position === 'after' ? ['', ...suffixColumns] : [...suffixColumns, ''],
      );
    }
  };

  const removeColumn = (side: 'prefix' | 'suffix', index: number) => {
    if (side === 'prefix') {
      setPrefixColumns(prefixColumns.filter((_, i) => i !== index));
    } else {
      setSuffixColumns(suffixColumns.filter((_, i) => i !== index));
    }
  };

  return (
    <Container>
      <SectionLabel>Base Word</SectionLabel>
      <StyledTextarea
        value={baseWordInput}
        onChange={(e) => setBaseWordInput(e.target.value)}
        placeholder="e.g. struct"
        rows={1}
      />

      <SectionLabel>Prefix Columns</SectionLabel>
      <Hint>Separate items with commas</Hint>
      <AddColumnButton onClick={() => addColumn('prefix', 'before')}>
        <PlusIcon size={14} /> Add Column Before
      </AddColumnButton>
      {prefixColumns.map((col, i) => (
        <ColumnRow key={i}>
          <StyledTextarea
            value={col}
            onChange={(e) => updateColumn('prefix', i, e.target.value)}
            placeholder={`Column ${i + 1}: e.g. re, de`}
            rows={2}
          />
          {prefixColumns.length > 1 && (
            <RemoveButton onClick={() => removeColumn('prefix', i)}>
              <XIcon size={14} />
            </RemoveButton>
          )}
        </ColumnRow>
      ))}
      <AddColumnButton onClick={() => addColumn('prefix', 'after')}>
        <PlusIcon size={14} /> Add Column After
      </AddColumnButton>

      <SectionLabel>Suffix Columns</SectionLabel>
      <Hint>Separate items with commas</Hint>
      <AddColumnButton onClick={() => addColumn('suffix', 'before')}>
        <PlusIcon size={14} /> Add Column Before
      </AddColumnButton>
      {suffixColumns.map((col, i) => (
        <ColumnRow key={i}>
          <StyledTextarea
            value={col}
            onChange={(e) => updateColumn('suffix', i, e.target.value)}
            placeholder={`Column ${i + 1}: e.g. ing, ed, s`}
            rows={2}
          />
          {suffixColumns.length > 1 && (
            <RemoveButton onClick={() => removeColumn('suffix', i)}>
              <XIcon size={14} />
            </RemoveButton>
          )}
        </ColumnRow>
      ))}
      <AddColumnButton onClick={() => addColumn('suffix', 'after')}>
        <PlusIcon size={14} /> Add Column After
      </AddColumnButton>

      <Button size="sm" onClick={handleSave} className="mt-3 w-full">
        Apply
      </Button>
    </Container>
  );
};

export default WordMatrixControlPanel;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: hsl(var(--foreground));
  margin-top: 8px;

  &:first-child {
    margin-top: 0;
  }
`;

const ColumnRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`;

const RemoveButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-top: 4px;
  border: 1px solid hsl(var(--border));
  border-radius: var(--radius);
  background: hsl(var(--background));
  color: hsl(var(--muted-foreground));
  cursor: pointer;

  &:hover {
    background: hsl(var(--destructive) / 0.1);
    color: hsl(var(--destructive));
    border-color: hsl(var(--destructive) / 0.3);
  }
`;

const AddColumnButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: hsl(var(--primary));
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 0;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid hsl(var(--border));
  border-radius: var(--radius);
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  background-color: hsl(var(--background));
  color: hsl(var(--foreground));

  &:focus {
    outline: none;
    border-color: hsl(var(--ring));
    box-shadow: 0 0 0 2px hsl(var(--ring) / 0.2);
  }

  &::placeholder {
    color: hsl(var(--muted-foreground));
  }
`;

const Hint = styled.p`
  font-size: 12px;
  color: hsl(var(--muted-foreground));
  margin: 0;
`;
